import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth';
import { BILLING_OVERVIEW_KEY, billingApi } from '@/lib/billing-api';
import type { TrialState } from '@/types/api';

/** Roles that operate the platform rather than subscribe to it. Mirrors Actor::PLATFORM_ROLES. */
const PLATFORM_ROLES = ['super_admin', 'admin', 'support'];

export interface SubscriptionAccess {
  /**
   * Whether paid actions may be attempted. True while loading and for staff — a control must
   * never be disabled on a guess. The server refuses with 402 regardless, so being wrong this
   * way costs one rejected request, whereas the opposite blocks a paying customer from a
   * feature they are entitled to.
   */
  permitsChanges: boolean;
  /** Staff hold no subscription, so nothing about one should be shown to them. */
  isStaff: boolean;
  /** True once a real answer is available, so callers can avoid flashing a notice. */
  isLoaded: boolean;
  /**
   * Why changes are blocked, phrased for a customer, or null when they are not.
   *
   * Taken verbatim from the API — the same verdict the enforcing listener applies. Deriving it
   * here instead produced two different answers to the same question, and the local one was
   * wrong twice: it echoed a stale "active" status for a lapsed term, and then failed to know
   * about suspension at all.
   */
  reason: string | null;
  /**
   * Whether the customer can lift the block themselves by paying. False for a suspension,
   * which only support can lift — so nothing should offer them a checkout for it.
   */
  selfServiceable: boolean;
  /**
   * Whether the platform has stopped doing scheduled work for this account: monitor checks,
   * scheduled backups, restore tests and metrics collection.
   *
   * The API pauses those for an account that had a subscription and has none granting access now.
   * An account that never had one has nothing running to pause, and telling it otherwise would only
   * confuse a new customer — so a subscription that once started a term is what decides it here.
   */
  servicesPaused: boolean;
  /** Where the account stands with the free trial. Null for staff and until billing has loaded. */
  trial: TrialState | null;
  /**
   * When a renewal payment has failed and the provider is retrying: the latest date everything keeps
   * working until. Null otherwise. Access is still granted meanwhile, so nothing else would say so.
   */
  pastDueUntil: string | null;
}

export function useSubscriptionAccess(): SubscriptionAccess {
  const { user } = useAuth();
  const isStaff = user !== null && PLATFORM_ROLES.includes(user.role);

  const billing = useQuery({
    queryKey: BILLING_OVERVIEW_KEY,
    queryFn: () => billingApi.overview(),
    staleTime: 30_000,
    enabled: !isStaff,
  });

  if (isStaff || !billing.isSuccess) {
    return {
      permitsChanges: true,
      isStaff,
      isLoaded: isStaff,
      reason: null,
      selfServiceable: false,
      servicesPaused: false,
      trial: null,
      pastDueUntil: null,
    };
  }

  const access = billing.data.access;
  const granting = billing.data.subscriptions.find((subscription) => subscription.id === access.subscription_id);

  return {
    permitsChanges: access.permitted,
    isStaff: false,
    isLoaded: true,
    reason: access.permitted ? null : access.reason,
    selfServiceable: access.status !== 'suspended',
    servicesPaused:
      !access.permitted && billing.data.subscriptions.some((subscription) => subscription.current_period_start !== null),
    trial: billing.data.trial,
    pastDueUntil: access.permitted && access.status === 'past_due' ? (granting?.access_ends_at ?? null) : null,
  };
}
